import * as React from "react";
import {
  InputAdornment,
  Button,
  createStyles,
  makeStyles,
  Theme,
  Grid,
  Hidden,
  ButtonBase,
  DialogContent,
  DialogActions,
  Typography,
} from "@material-ui/core";
import SimpleTextField from "../../../components/SimpleTextField";
import { useHubx, useHubXSubscribe } from "../../../HubX";
import {
  EVENT_ALL_INSTALLATIONS_ALREADY_LOADED,
  EVENT_ALL_INSTALLATIONS_PAGE_FILTER,
} from "../../../Events";
import SearchIcon from "@material-ui/icons/Search";
import useConstructor from "../../../core/hooks/useConstructor";
import { FETCH_ALL_INSTALLATIONS_FILTERS } from "../../../Fedx";
import { useSnackbar } from "notistack";
import AllInstallationsFilterType, {
  AllInstallationsFilterGet,
} from "../../../core/models/AllInstallationsFilter";
import BaseFilter from "../../../core/models/BaseFilter";
import { capitalize, sortBy } from "lodash";
import { getLevelColor, getLevelStatusString } from "../../../helpers/installationTankHelpers";
import {
  installationLevelStatus,
  INSTALLATION_LEVEL_STATUS_TYPES,
} from "../../../core/models/LevelStatus";
import {
  installationOrderType,
  INSTALLATION_ORDER_TYPE_MAP,
  INSTALLATION_ORDER_TYPE_TYPES,
  orderType,
} from "../../../core/models/OrderType";
import { tripStatus, TRIP_STATUS_TYPES } from "../../../core/models/TripStatus";
import MaterialSelect from "../../../components/MaterialSelect";
import { Skeleton } from "@material-ui/lab";
import FilterIcon from "../../../assets/icons/FilterIcon";
import SimpleDialog from "../../../components/SimpleDialog";
import clsx from "clsx";
import { isArrayValid, toggleElementAll } from "../../../helpers/utility";
import { isSafari } from "../../../helpers/deviceDetect";
import Loader from "react-loader-spinner";
import { LocalCache } from "../../../helpers/cache";
import { DEFAULT_RELOAD_MINUTES } from "../../../core/constants/appGlobals";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    filterSkeleton: {
      minWidth: 125,
      minHeight: 40,
      height: "100%",
    },

    buttonsContainer: {
      "display": "flex",
      "flex": 1,
      "alignItems": "center",
      "justifyContent": "space-around",
      "padding": theme.spacing(0, 2),
      "columnGap": `${theme.spacing(1)}px`,

      "& button": {
        width: "max-content",
      },
    },

    searchBar: {
      "display": "flex",
      "justifyContent": "space-between",
      "alignItems": "center",
      "padding": theme.spacing(1, 4),
      "background": theme.customPaper.background.cardBackgroundLight,
      "divShadow": "0px 2px 3px #0000000D",
      "border": `1px solid ${theme.customPaper.background.cardBackgroundLight}`,
      "borderRadius": 1,

      "& .MuiFormControl-root": {
        marginTop: theme.spacing(1.25),
      },

      [theme.breakpoints.between("sm", "lg")]: {
        padding: theme.spacing(1, 2),
      },

      [theme.breakpoints.only("sm")]: {
        padding: theme.spacing(1.25, 2),
      },

      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(0.5),
      },
    },

    searchBarMobile: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(1, 4),
      background: theme.customPaper.background.cardBackgroundLight,
      divShadow: "0px 2px 3px #0000000D",
      border: `1px solid ${theme.customPaper.background.cardBackgroundLight}`,
      borderRadius: 1,

      [theme.breakpoints.between("sm", "lg")]: {
        padding: theme.spacing(1, 2),
      },

      [theme.breakpoints.only("sm")]: {
        padding: theme.spacing(1.25, 2),
      },

      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(0.5),
      },
    },

    searchBarContainerMobile: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexDirection: "row",
      paddingTop: theme.spacing(0.5),
    },

    buttonsContainerMobile: {
      width: "100%",
      display: "flex",
      justifyContent: "flex-end",
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(0.5),
    },

    filterBtnMobile: {
      marginLeft: theme.spacing(0.5),
    },

    categoryText: {
      fontSize: "1rem",
      fontWeight: "bold",
      color: theme.customPalette.text.darkgrey,
    },

    menuWrapper: {
      "borderLeft": "1px dashed #707070",
      "flex": 1,
      "display": "flex",
      "justifyContent": "center",
      "alignItems": "center",
      "paddingLeft": theme.spacing(1),
      "marginLeft": theme.spacing(1),

      "&.disable-border": {
        borderLeft: "unset",
      },

      [theme.breakpoints.down("sm")]: {
        borderLeft: "unset",
      },
    },

    mobileDialog: {
      "display": "flex",
      "justifyContent": "center",
      "alignItems": "center",
      "padding": theme.spacing(1),

      "& > *": {
        flexDirection: "column",
        rowGap: `${theme.spacing(3)}px`,
      },
    },

    filterItem: {
      "marginBottom": theme.spacing(2),
      "&:nth-child(2)": {
        marginTop: theme.spacing(2),
      },
    },
  })
);

const mockFilterLoading = new Array(5).fill({});

function AllInstallationsFilter() {
  const classes = useStyles();
  const hub = useHubx();
  const toastr = useSnackbar();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [loadingInstallations, setLoadingInstallations] = React.useState<boolean>(false);
  const [nameOrId, setNameOrID] = React.useState<string>("");
  const [filtersOptions, setFiltersOptions] = React.useState<null | AllInstallationsFilterGet>(
    null
  );
  const [selectedStatus, setSelectedStatus] = React.useState<installationLevelStatus[] | "all">(
    "all"
  );
  const [selectedProduct, setSelectedProduct] = React.useState<string | string[]>(["all"]);
  const [selectedUF, setSelectedUF] = React.useState<string[]>(["all"]);
  const [selectedCDL, setSelectedCDL] = React.useState<string[]>(["all"]);
  const [selectedMarket, setSelectedMarket] = React.useState<string[]>(["all"]);
  const [selectedVendor, setSelectedVendor] = React.useState<string[]>(["all"]);
  const [selectedTripStatus, setSelectedTripStatus] = React.useState<tripStatus[] | "all">("all");
  const [selectedOrderType, setSelectedOrderType] = React.useState<installationOrderType[] | "all">("all");
  const [mobileDialogFiltersOpen, setMobileDialogFiltersOpen] = React.useState<boolean>(false);
  const [installationFilter, setInstallationFilter] = React.useState<AllInstallationsFilterType>();

  const localCache = new LocalCache();
  const CACHE_HOLDER_NAME = "allInstallationsFilter";

  const fetchFilters = async () => {
    setLoading(true);
    try {
      const filters = await hub.request(FETCH_ALL_INSTALLATIONS_FILTERS);
      setFiltersOptions(filters);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("🚀 ~ file: index.tsx ~ line 43 ~ fetchFilters ~ error", error);
      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao obter as opções dos filtros!");
    } finally {
      setLoading(false);
    }
  };

  const getFilterFromCache = () => {
    return new Promise((resolve, reject) => {
      const tempFilter = localCache.get<any>(CACHE_HOLDER_NAME);
      if (tempFilter) {
        setNameOrID(tempFilter.nameOrId);
        setSelectedCDL(tempFilter.selectedCDL);
        setSelectedProduct(tempFilter.selectedProduct);
        setSelectedStatus(tempFilter.selectedStatus);
        setSelectedTripStatus(tempFilter.selectedTripStatus);
        setSelectedUF(tempFilter.selectedUF);
        setSelectedMarket(tempFilter.selectedMarket);
        setSelectedVendor(tempFilter.selectedVendor);
        setSelectedOrderType(tempFilter.selectedOrderType);
        resolve(true);
      }
      resolve(true);
    });
  };

  const showClearFilter =
    nameOrId ||
    [
      selectedProduct,
      selectedUF,
      selectedMarket,
      selectedCDL,
      selectedStatus,
      selectedVendor,
      selectedTripStatus,
      selectedOrderType,
    ].some((x) => x != "all" && x != ["all"]);

  useConstructor(async () => {
    await fetchFilters();
    const start = async () => {
      await getFilterFromCache();
    };
    start();
  });

  const MINUTE_MS = 60000;

  React.useMemo(() => {
  
    const filters = {
      installation_name_id: nameOrId,
      installation_product: selectedProduct === ["all"] ? undefined : (selectedProduct as string[]),
      installation_uf: selectedUF === ["all"] ? undefined : (selectedUF as string[]),
      installation_ldc: selectedCDL === ["all"] ? undefined : (selectedCDL as string[]),
      installation_market: selectedMarket === ["all"] ? undefined : (selectedMarket as string[]),
      installation_vendor: selectedVendor === ["all"] ? undefined : (selectedVendor as string[]),
      installation_orderType : selectedOrderType === ["all"] ? undefined : (selectedOrderType as string[]),
      installation_alert:
        selectedStatus === "all" ? undefined : (selectedStatus as installationLevelStatus[]),
      installation_trip_status:
        selectedTripStatus === "all" ? undefined : (selectedTripStatus as tripStatus[]),
    }
    setInstallationFilter(filters);
  }, [nameOrId, selectedProduct, selectedUF, selectedCDL, selectedMarket, selectedVendor, selectedStatus, selectedTripStatus, selectedOrderType]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      hub.notify(EVENT_ALL_INSTALLATIONS_PAGE_FILTER, { filters: installationFilter, loading: false });
    }, DEFAULT_RELOAD_MINUTES * MINUTE_MS);

    return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
  }, [installationFilter])

  const handleClickSearch = (loading = true) => {
    hub.notify(EVENT_ALL_INSTALLATIONS_PAGE_FILTER, { filters: installationFilter, loading });
    holdFilter();
  };

  useHubXSubscribe(EVENT_ALL_INSTALLATIONS_ALREADY_LOADED, () => {
    setLoadingInstallations(!loadingInstallations);
  });

  const handleChangeProduct = (e: React.ChangeEvent<{ value: string | string[] }>) => {
    const newValue = toggleElementAll(e.target.value as string[]);

    setSelectedProduct(newValue);
  };

  const handleChangeUF = (e: React.ChangeEvent<{ value: string | string[] }>) => {
    const newValue = toggleElementAll(e.target.value as string[]);
    setSelectedUF(newValue);
  };

  const handleChangeCDL = (e: React.ChangeEvent<{ value: string | string[] }>) => {
    const newValue = toggleElementAll(e.target.value as string[]);
    setSelectedCDL(newValue);
  };

  const handleChangeMarket = (e: React.ChangeEvent<{ value: string | string[] }>) => {
    const newValue = toggleElementAll(e.target.value as string[]);
    setSelectedMarket(newValue);
  };

  const handleChangeStatus = (e: React.ChangeEvent<{ value: string | string[] }>) => {
    const newValue = e.target.value;
    setSelectedStatus(toggleElementAll(newValue as string[]) as installationLevelStatus[] | "all");
  };

  const handleChangeVendor = (e: React.ChangeEvent<{ value: string | string[] }>) => {
    const newValue = toggleElementAll(e.target.value as string[]);
    setSelectedVendor(newValue);
  };

  const handleChangeTripStatus = (e: React.ChangeEvent<{ value: string | string[] }>) => {
    const newValue = e.target.value;
    setSelectedTripStatus(toggleElementAll(newValue as string[]) as tripStatus[] | "all");
  };

const handleChangeOrderType =  (e: React.ChangeEvent<{ value: string | string[] }>) => {
  const newValue = e.target.value;
  setSelectedOrderType(toggleElementAll(newValue as string[]) as installationOrderType[] | "all");
};

  const filtersMap: BaseFilter[] = React.useMemo(() => {
    return [
      {
        id: "product",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...sortBy(filtersOptions?.installation_product ?? [])?.map((x: string) => {
            return { key: x, value: x };
          }),
        ],
        label: "Produto",
        multiple: true,
        value: selectedProduct,
        handleChange: handleChangeProduct,
      },
      {
        id: "uf",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...sortBy(filtersOptions?.installation_uf ?? [])?.map((x: string) => {
            return { key: x, value: x };
          }),
        ],
        label: "Estado",
        multiple: true,
        value: selectedUF,
        handleChange: handleChangeUF,
      },
      {
        id: "responsible_cdl",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...sortBy(filtersOptions?.installation_cdl ?? [])?.map((x: string) => {
            return { key: x, value: x };
          }),
        ],
        label: "CDL Responsável",
        multiple: true,
        width: 135,
        value: selectedCDL,
        handleChange: handleChangeCDL,
      },
      {
        id: "market_segmentation",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...sortBy(filtersOptions?.installation_market ?? [])?.map((x: string) => {
            return { key: x, value: x };
          }),
        ],
        label: "Segmento de Mercado",
        multiple: true,
        width: 165,
        value: selectedMarket,
        handleChange: handleChangeMarket,
      },
      {
        id: "status",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...INSTALLATION_LEVEL_STATUS_TYPES.map((x) => {
            return {
              key: x,
              value: (
                <span>
                  <span style={{ color: `${getLevelColor(x)}` }}>●</span> {getLevelStatusString(x)}
                </span>
              ),
            };
          }),
        ],
        label: "Status",
        multiple: true,
        value: selectedStatus,
        handleChange: handleChangeStatus,
      },
      {
        id: "vendor",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...sortBy(filtersOptions?.installation_vendor ?? [])?.map((x: string) => {
            return { key: x, value: x };
          }),
        ],
        label: "Fabricante RTU",
        multiple: true,
        value: selectedVendor,
        handleChange: handleChangeVendor,
      },
      {
        id: "trip_status",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...TRIP_STATUS_TYPES?.map((x: string) => {
            return { key: x, value: capitalize(x) };
          }),
        ],
        label: "Viagem",
        multiple: true,
        value: selectedTripStatus,
        handleChange: handleChangeTripStatus,
      },
      {
        id: "order_installation",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...INSTALLATION_ORDER_TYPE_TYPES.map((x:string) => {
              return { key: INSTALLATION_ORDER_TYPE_MAP[x as orderType].toLocaleLowerCase(), value: capitalize(x) };
          }),
        
        ],
        label: "Tipo de Pedido",
        multiple: true,
        value: selectedOrderType,
        handleChange: handleChangeOrderType,
      },
    ];
  }, [
    filtersOptions?.installation_product,
    filtersOptions?.installation_uf,
    filtersOptions?.installation_vendor,
    selectedProduct,
    selectedStatus,
    selectedTripStatus,
    selectedUF,
    selectedCDL,
    selectedMarket,
    selectedVendor,
    selectedOrderType,
  ]);

  const clearFilter = () => {
    setNameOrID("");
    setSelectedProduct(["all"]);
    setSelectedUF(["all"]);
    setSelectedCDL(["all"]);
    setSelectedMarket(["all"]);
    setSelectedStatus("all");
    setSelectedVendor(["all"]);
    setSelectedTripStatus("all");
    setSelectedOrderType("all");
    localCache.delete(CACHE_HOLDER_NAME);
  };

  const holdFilter = () => {
    const savedFilter = {
      nameOrId,
      selectedProduct,
      selectedStatus,
      selectedTripStatus,
      selectedUF,
      selectedCDL,
      selectedMarket,
      selectedVendor,
      selectedOrderType,
    };
    localCache.set(CACHE_HOLDER_NAME, savedFilter);
  };

  return (
    <>
      <Hidden smDown>
        <div className={classes.searchBar}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <SimpleTextField
                value={nameOrId}
                onChange={(e) => setNameOrID(e.target.value)}
                placeholder="Nome fantasia ou ID"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {!loading &&
              filtersMap?.map((filter) => (
                <MaterialSelect key={filter.id} filter={filter} hasCheckbox />
              ))}

            {loading &&
              mockFilterLoading.map((_, idx) => (
                <Grid key={`filter_skeleton_${idx}`} item>
                  <Skeleton className={classes.filterSkeleton} />
                </Grid>
              ))}
          </Grid>

          <div className={classes.buttonsContainer}>
            {showClearFilter && (
              <Button onClick={clearFilter} variant="outlined">
                Limpar Filtros
              </Button>
            )}
            {loadingInstallations ? (
              <div>
                <Loader type="TailSpin" color="#00AB66" height={15} width={15} />
              </div>
            ) : (
              <Button
                disabled={loadingInstallations}
                onClick={() => handleClickSearch()}
                variant="outlined"
              >
                Buscar
              </Button>
            )}
          </div>
        </div>
      </Hidden>

      {/* MOBILE VERSION */}
      <Hidden smUp>
        <div className={classes.searchBarMobile}>
          <div className={classes.searchBarContainerMobile}>
            <SimpleTextField
              value={nameOrId}
              onChange={(e) => setNameOrID(e.target.value)}
              placeholder="Nome fantasia ou ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {loadingInstallations ? (
              <div>
                <Loader type="TailSpin" color="#00AB66" height={15} width={15} />
              </div>
            ) : (
              <Button
                disabled={loadingInstallations}
                onClick={() => handleClickSearch()}
                variant="outlined"
              >
                Buscar
              </Button>
            )}
          </div>

          <div className={classes.buttonsContainerMobile}>
            {showClearFilter && (
              <Button onClick={clearFilter} variant="outlined">
                Limpar Filtros
              </Button>
            )}
            <ButtonBase
              className={classes.filterBtnMobile}
              onClick={() => setMobileDialogFiltersOpen(!mobileDialogFiltersOpen)}
            >
              <FilterIcon />
              <Button
                disableFocusRipple
                disableTouchRipple
                disableRipple
                variant="text"
                color="secondary"
                component="span"
              >
                Filtros
              </Button>
            </ButtonBase>
          </div>
        </div>
      </Hidden>

      <SimpleDialog
        draggable={false}
        id="mobile-home-filter"
        open={mobileDialogFiltersOpen}
        handleClose={() => setMobileDialogFiltersOpen(false)}
        maxWidth="xs"
        title="Filtrar"
      >
        <DialogContent className={classes.mobileDialog}>
          <div className={clsx(classes.menuWrapper)}>
            <Typography className={classes.categoryText}>Filtros: </Typography>
            {!loading &&
              filtersMap?.map((filter) => (
                <div key={filter.id} className={isSafari ? classes.filterItem : ""}>
                  <MaterialSelect filter={filter} hasCheckbox />
                </div>
              ))}
            {loading &&
              mockFilterLoading.map((_, idx) => (
                <Grid key={`filter_skeleton_${idx}`} item>
                  <Skeleton className={classes.filterSkeleton} />
                </Grid>
              ))}
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={loading}
            variant="contained"
            onClick={(e) => {
              handleClickSearch();
              setMobileDialogFiltersOpen(false);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </SimpleDialog>
    </>
  );
}

export default AllInstallationsFilter;
